import classnames from "classnames";
import { find } from "lodash";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";

import SortSVG from "@krea/customer-features/images/svg/filter.svg";
import { ISortBidsBy } from "@krea/customer-features/utils";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { AppModal } from "@krea/shared/design-system/modal";

export interface ISortingOptions {
  title: string;
  key:
    | "attractivenessScore"
    | "amount"
    | "paybackTimeMonths"
    | "monthlyCost"
    | "firstMonthInterestRate"
    | "comparableMonthlyInterestRate"
    | "comparableYearlyInterestRate"
    | "limit"
    | "monthlyPayment"
    | "purchasedInvoiceAmount"
    | "commissionPercent"
    | "totalPaidOut"
    | "withRegress";
  order: "asc" | "desc";
}

interface IBidsSort {
  className?: string;
  sortBy: ISortBidsBy;
  setSortBy: Dispatch<SetStateAction<ISortBidsBy>>;
  options: ISortingOptions[];
}

const BidsSort = ({ className, sortBy, setSortBy, options }: IBidsSort) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(-100);

  return (
    <div
      role="button"
      tabIndex={0}
      className={twMerge(
        "tw-flex tw-align-middle tw-border-solid tw-border tw-border-primary tw-bg-primary-150 tw-rounded-full tw-py-1 px-2",
        className,
      )}
      onKeyDown={() => {}}
      onClick={() => setOpen(true)}
    >
      <div className="tw-mr-3 tw-flex tw-align-middle">
        <div className="tw-relative tw-mr-2 tw-block tw-w-[16px] before:tw-p-0 before:tw-pb-[100%]">
          <SVG
            className="tw-absolute tw-left-0 tw-top-0 tw-block tw-h-full tw-w-full"
            src={SortSVG}
          />
        </div>
        <div
          className={classnames(
            "tw-text-nowrap tw-text-sm tw-font-semibold tw-leading-normal tw-text-primary",
          )}
        >
          {t("dash.sortOffers")}
        </div>
      </div>

      <AppModal
        open={open}
        setOpen={setOpen}
        onClose={() => {
          setSortBy(sortBy);
          setTimeout(() => {
            setOpen(false);
          }, 100);
        }}
        className="tw-mb-2"
        title={t("dash.sortOffers")}
      >
        {options.map((item, index) => (
          <div
            role="button"
            tabIndex={0}
            onKeyDown={() => {}}
            key={index}
            className={classnames(
              "tw-my-1 tw-flex tw-rounded-md tw-p-1 tw-px-3 tw-accent-primary",
              {
                "tw-bg-[#fbf2ff]": checked === index,
              },
            )}
            onClick={() => {
              setChecked(index);
              setSortBy({
                field: item.key,
                order:
                  find(options, (option) => option.key === item.key)?.order ??
                  "desc",
              });
              setTimeout(() => {
                setOpen(false);
              }, 100);
            }}
          >
            <label>
              <input
                type="radio"
                id={index.toString()}
                name={item.title}
                value={item.key}
                className="tw-mr-2"
                checked={checked === index}
              />
              {item.title}
            </label>
          </div>
        ))}
      </AppModal>
    </div>
  );
};

export default BidsSort;
